import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Download, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, MapPin, ShieldAlert, FileText } from "lucide-react";

const SEV_COLOR = {
  critical: "#FF2E63",
  high:     "#FF6B35",
  medium:   "#FFC542",
  low:      "#00F0FF",
};

export default function ShiftBriefings() {
  const [briefings, setBriefings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  async function loadList() {
    setLoading(true);
    try {
      const r = await api.get("/shifts/?limit=30");
      setBriefings(r.data.briefings || []);
      if (!selectedId && r.data.briefings?.length) {
        setSelectedId(r.data.briefings[0].id);
      }
    } catch (e) {
      toast.error("Failed to load briefings");
    } finally {
      setLoading(false);
    }
  }

  async function loadOne(id) {
    try {
      const r = await api.get(`/shifts/${id}`);
      setSelected(r.data);
    } catch { setSelected(null); }
  }

  async function generate() {
    setGenerating(true);
    try {
      const r = await api.post("/shifts/generate");
      toast.success(`Briefing generated: ${r.data.shift_label}`);
      await loadList();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    } finally { setGenerating(false); }
  }

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedId) loadOne(selectedId); }, [selectedId]);

  const totals = selected?.totals || {};
  const sev = totals.severity_breakdown || {};
  const delta = totals.delta_pct || 0;

  const downloadPdf = () => {
    if (!selectedId) return;
    const token = localStorage.getItem("forensiq_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shifts/${selectedId}/pdf`;
    // Simple approach: use fetch → blob → download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `shift_${selectedId.slice(0, 8)}.pdf`;
        a.click();
      })
      .catch(() => toast.error("PDF download failed"));
  };

  return (
    <div className="min-h-screen bg-[#060606] text-white" data-testid="shifts-page">
      <div className="px-8 pt-8 pb-5 border-b border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
              <h1 className="font-display font-black text-3xl tracking-tight">SHIFT BRIEFINGS</h1>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] px-2 py-0.5 border border-white/10 rounded-sm">
                every 8h · 00 · 08 · 16 UTC
              </span>
            </div>
            <p className="text-sm text-[#8E9196] mt-1 max-w-2xl">
              A one-page recap of what happened while you were away — spikes, new hotspots, real IOCs, and open critical cases.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={generate}
              disabled={generating}
              data-testid="shifts-generate-btn"
              className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#00F0FF]/50 rounded-sm text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Generating…" : "Generate now"}
            </button>
            <button
              onClick={downloadPdf}
              disabled={!selectedId}
              data-testid="shifts-pdf-btn"
              className="flex items-center gap-2 px-3 py-2 border border-[#00F0FF]/40 hover:border-[#00F0FF] rounded-sm text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] transition-colors disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[280px_1fr] gap-0" style={{ minHeight: "calc(100vh - 148px)" }}>
        {/* List of briefings */}
        <aside className="border-r border-white/10 bg-[#0a0a0a] overflow-y-auto" data-testid="shifts-list">
          {loading && <div className="p-6 text-xs text-[#52525B] font-mono">Loading…</div>}
          {!loading && briefings.length === 0 && (
            <div className="p-6 text-xs text-[#52525B] font-mono">
              No briefings yet. Click <b className="text-white">Generate now</b> to create the first one.
            </div>
          )}
          {briefings.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedId(b.id)}
              className={`w-full text-left px-4 py-3 border-b border-white/[0.04] transition-colors ${
                selectedId === b.id ? "bg-[#00F0FF]/10" : "hover:bg-white/[0.03]"
              }`}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                {b.shift_label}
              </div>
              <div className="mt-1 text-xs text-white/90 truncate">
                {b.totals?.events || 0} events · CRIT {b.totals?.severity_breakdown?.critical || 0}
              </div>
              {(b.totals?.delta_pct || 0) !== 0 && (
                <div className="mt-0.5 text-[10px] font-mono flex items-center gap-1"
                     style={{ color: (b.totals?.delta_pct || 0) > 0 ? "#FF6B35" : "#00F0FF" }}>
                  {(b.totals?.delta_pct || 0) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {b.totals?.delta_pct > 0 ? "+" : ""}{b.totals?.delta_pct}%
                </div>
              )}
            </button>
          ))}
        </aside>

        {/* Detail */}
        <div className="p-8 overflow-y-auto" data-testid="shifts-detail">
          {!selected ? (
            <div className="text-center text-[#52525B] font-mono text-sm mt-16">Select a briefing to view its details.</div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Shift</div>
                <h2 className="font-display font-black text-2xl">{selected.shift_label}</h2>
                <div className="text-[10px] font-mono text-[#52525B] mt-1">Generated {new Date(selected.generated_at).toLocaleString()}</div>
              </div>

              {/* Highlights */}
              <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#FFC542]" /> Highlights
                </div>
                <ul className="space-y-2">
                  {(selected.highlights || []).map((h, i) => (
                    <li key={i} className="text-sm text-white/90 flex gap-2">
                      <span className="text-[#00F0FF]">•</span>{h}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Totals grid */}
              <div className="grid grid-cols-6 gap-3">
                {["critical", "high", "medium", "low"].map((k) => (
                  <div key={k} className="border border-white/10 rounded-sm p-3 text-center bg-[#0a0a0a]">
                    <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: SEV_COLOR[k] }}>{k}</div>
                    <div className="font-display font-black text-2xl mt-1" style={{ color: SEV_COLOR[k] }}>{sev[k] || 0}</div>
                  </div>
                ))}
                <div className="border border-white/10 rounded-sm p-3 text-center bg-[#0a0a0a]">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">total</div>
                  <div className="font-display font-black text-2xl mt-1 text-white">{totals.events || 0}</div>
                </div>
                <div className="border border-white/10 rounded-sm p-3 text-center bg-[#0a0a0a]">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">vs prev</div>
                  <div className="font-display font-black text-2xl mt-1 flex items-center justify-center gap-1"
                       style={{ color: delta > 0 ? "#FF6B35" : delta < 0 ? "#00F0FF" : "#FFF" }}>
                    {delta > 0 ? "+" : ""}{delta}%
                  </div>
                </div>
              </div>

              {/* Hotspots + Real IOCs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#00F0FF]" /> Top Origin Hotspots
                  </div>
                  {(selected.hotspots || []).length === 0 && (
                    <div className="text-xs text-[#52525B] font-mono">None recorded for this shift.</div>
                  )}
                  {(selected.hotspots || []).map((h, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <div className="text-sm">{h.city}, <span className="text-[#8E9196]">{h.country}</span></div>
                      <div className="font-mono text-xs" style={{
                        color: h.max_severity_weight >= 4 ? "#FF2E63" : h.max_severity_weight >= 3 ? "#FF6B35" : "#FFC542"
                      }}>{h.count}</div>
                    </div>
                  ))}
                </div>

                <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3 flex items-center gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 text-[#FF2E63]" /> Real IOCs
                  </div>
                  {(selected.real_iocs || []).length === 0 && (
                    <div className="text-xs text-[#52525B] font-mono">None flagged for this shift.</div>
                  )}
                  {(selected.real_iocs || []).slice(0, 6).map((r, i) => (
                    <div key={i} className="text-xs py-1 border-b border-white/[0.04] last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: SEV_COLOR[r.severity] }}>
                          {r.severity}
                        </span>
                        <span className="text-white/90">{r.attack_label}</span>
                      </div>
                      <div className="text-[10px] font-mono text-[#8E9196] mt-0.5">
                        {r.source?.city}/{r.source?.country} → {r.target?.city} · case {r.case_number || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Open critical investigations */}
              {(selected.open_critical_investigations || []).length > 0 && (
                <div className="border border-[#FF2E63]/30 rounded-sm p-5 bg-[#FF2E63]/[0.03]">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF2E63] mb-3 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Open Malicious Investigations
                  </div>
                  {selected.open_critical_investigations.map((o, i) => (
                    <div key={i} className="text-sm py-1">
                      <span className="font-mono text-[#00F0FF]">{o.case_number}</span>
                      <span className="text-white/90 mx-2">·</span>
                      <span className="text-white/90">{o.title}</span>
                      <span className="text-[10px] font-mono text-[#8E9196] ml-2">
                        {o.status} · conf {o.confidence}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
